import { Component, DestroyRef, ElementRef, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter } from 'rxjs/operators';
import { CatGraph, CatGraphSummary } from '../../../../generated.commonjs/cat_graph_pb';
import { ClientLoggingService } from '../../../shared/client-logging.service';
import {
  GraphCitations,
  ListedArgument,
  ListedClaim,
  chipDestination,
  graphCitations,
  listClaims,
} from '../../../shared/cat-graph/cat-graph-evidence';
import { CleanCatGraphPicture, cleanCatGraphPicture, pictureTarget } from '../../../shared/cat-graph/cat-graph-picture';
import { CatGraphService } from '../../../shared/cat-graph/cat-graph.service';

/** How the graph is shown. */
export type CatGraphViewMode = 'picture' | 'list';

/** Zoom steps of the picture, as a share of its own size. `fit` scales it to the width of the view. */
export const ZOOM_STEPS: readonly number[] = Object.freeze([0.5, 0.75, 1, 1.25, 1.5, 2]);

/** The graph segment of a project URL: `/projects/:planId/cat-graphs/:graphId`. */
const GRAPH_ID_IN_URL = /\/cat-graphs\/([^/?#]+)/;

/**
 * The project's CAT graphs (Issue #2399): a list of the graphs, and a view of one.
 *
 * A finding with a graph spans several sheets and code sections, so the graph cannot hang on one
 * page's compliance report. It has a place of its own, as a tab of the project page beside Tasks.
 *
 * The view shows the picture the cat-graph service draws (#2538), cleaned before it is shown (see
 * `cat-graph-picture.ts`). Behaviour is added to it by the identifiers it carries, through one click
 * handler and one key handler on the picture: a chip that names citations opens them, and a box can
 * be marked. When the picture cannot be fetched, the view lists the graph's claims and their
 * evidence from the graph's message, which it has in any case.
 */
@Component({
  selector: 'app-cat-graphs-page',
  imports: [
    RouterModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
  ],
  templateUrl: './cat-graphs-page.component.html',
  styleUrls: ['./cat-graphs-page.component.scss'],
})
export class CatGraphsPageComponent implements OnInit {
  private readonly catGraphs = inject(CatGraphService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);

  readonly projectId = signal('');
  readonly graphId = signal('');

  readonly summaries = signal<CatGraphSummary[]>([]);
  readonly listLoading = signal(false);
  readonly listError = signal('');

  readonly graph = signal<CatGraph | null>(null);
  readonly graphLoading = signal(false);
  readonly graphError = signal('');

  readonly picture = signal<CleanCatGraphPicture | null>(null);
  readonly pictureLoading = signal(false);
  /** Why the picture is not shown, or `''` while it is or may still be. */
  readonly pictureError = signal('');

  readonly mode = signal<CatGraphViewMode>('picture');
  /** A zoom step, or `'fit'` to scale the picture to the width of the view. */
  readonly zoom = signal<number | 'fit'>('fit');
  /** The box the reader marked, by argument and node ID. */
  readonly selected = signal<{ argumentId: string; nodeId: string } | null>(null, {
    // A click makes a new object for the same box; marking it again would change nothing.
    equal: (a, b) => a?.argumentId === b?.argumentId && a?.nodeId === b?.nodeId,
  });
  /** What the view last said about opening evidence, read out by screen readers. */
  readonly announcement = signal('');

  private readonly pictureElement = viewChild<ElementRef<HTMLElement>>('picture');

  /** The graph's citations, validated against the graph's own project. */
  readonly citations = computed<GraphCitations | null>(() => {
    const graph = this.graph();
    return graph ? graphCitations(graph) : null;
  });

  /** The graph's claims and their evidence, for the list. */
  readonly arguments = computed<ListedArgument[]>(() => {
    const graph = this.graph();
    return graph ? listClaims(graph) : [];
  });

  /** The marked claim, for the panel under the toolbar. */
  readonly selectedClaim = computed<ListedClaim | null>(() => {
    const selected = this.selected();
    if (!selected) return null;
    for (const argument of this.arguments()) {
      const claim = argument.claims.find(
        candidate => candidate.argumentId === selected.argumentId && candidate.nodeId === selected.nodeId
      );
      if (claim) return claim;
    }
    return null;
  });

  /**
   * The cleaned picture, trusted as a whole. This is the sanitize-then-trust rule of #1773: the
   * string trusted is exactly the document DOMPurify returned, never a part of it.
   */
  readonly pictureHtml = computed<SafeHtml | null>(() => {
    const picture = this.picture();
    return picture ? this.sanitizer.bypassSecurityTrustHtml(picture.svg) : null;
  });

  /** The picture's width in pixels at the current zoom, or `null` to fit the view. */
  readonly pictureWidth = computed<number | null>(() => {
    const zoom = this.zoom();
    const picture = this.picture();
    return zoom === 'fit' || !picture?.width ? null : Math.round(picture.width * zoom);
  });

  /** The picture's accessible name. */
  readonly pictureLabel = computed(() => {
    const title = this.graph()?.getTitle() || this.graphId();
    return `CAT graph: ${title}. Evidence chips are links; press Enter to open one.`;
  });

  private loadedProject = '';
  private requestedGraph = '';

  constructor() {
    // Marks the selected box in the picture. The picture's elements are the service's; the mark is
    // one attribute that the stylesheet of this component draws.
    effect(() => {
      const selected = this.selected();
      const element = this.pictureElement()?.nativeElement;
      this.pictureHtml();
      if (!element) return;
      for (const marked of Array.from(element.querySelectorAll('[data-selected-frame]'))) {
        marked.removeAttribute('data-selected-frame');
      }
      if (!selected) return;
      for (const box of Array.from(element.querySelectorAll('[data-node]'))) {
        if (
          box.getAttribute('data-node') === selected.nodeId &&
          box.getAttribute('data-argument') === selected.argumentId
        ) {
          // A box group holds its frame in its first inner group: the frame is that group's first rectangle.
          box.querySelector(':scope > g > rect')?.setAttribute('data-selected-frame', 'true');
        }
      }
    });
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.projectId.set(params['planId'] || '');
      this.syncFromUrl();
    });
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.syncFromUrl());
    this.syncFromUrl();
  }

  /** The route of one graph. */
  graphLink(graphId: string): string[] {
    return ['/projects', this.projectId(), 'cat-graphs', graphId];
  }

  /** The route of the list. */
  listLink(): string[] {
    return ['/projects', this.projectId(), 'cat-graphs'];
  }

  setMode(mode: CatGraphViewMode): void {
    this.mode.set(mode);
  }

  /**
   * The next larger step. "Fit width" can show the picture above the largest step or below the
   * smallest, so when no step lies in the chosen direction the zoom stays as it is.
   */
  zoomIn(): void {
    const larger = ZOOM_STEPS.find(step => step > this.currentZoom());
    if (larger !== undefined) this.zoom.set(larger);
  }

  /** The next smaller step, or no change when there is none. */
  zoomOut(): void {
    const smaller = ZOOM_STEPS.filter(step => step < this.currentZoom());
    if (smaller.length) this.zoom.set(smaller[smaller.length - 1]);
  }

  fitWidth(): void {
    this.zoom.set('fit');
  }

  /** The zoom as a number: the share of its own size the picture is shown at. */
  currentZoom(): number {
    const zoom = this.zoom();
    if (zoom !== 'fit') return zoom;
    const picture = this.picture();
    const shown = this.pictureElement()?.nativeElement.clientWidth ?? 0;
    return picture?.width && shown ? shown / picture.width : 1;
  }

  /** A click in the picture: opens a chip's evidence, and marks the box it was in. */
  onPictureClick(event: MouseEvent): void {
    const element = this.pictureElement()?.nativeElement;
    if (!element) return;
    const target = pictureTarget(event.target, element);
    if (target.box) this.selected.set(target.box);
    if (target.chip) {
      event.preventDefault();
      void this.openEvidence(target.chip.citationIds);
    }
  }

  /** Enter or Space on a chip opens it, as a link would. */
  onPictureKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const element = this.pictureElement()?.nativeElement;
    if (!element) return;
    const target = pictureTarget(event.target, element);
    if (!target.chip) return;
    event.preventDefault();
    if (target.box) this.selected.set(target.box);
    void this.openEvidence(target.chip.citationIds);
  }

  /** A piece of evidence in the list of claims: marks its claim, then opens the evidence. */
  selectAndOpenEvidence(claim: ListedClaim, citationIds: readonly string[]): void {
    this.selectClaim(claim);
    void this.openEvidence(citationIds);
  }

  /** Marks a claim of the list in the picture as well. */
  selectClaim(claim: ListedClaim): void {
    this.selected.set({ argumentId: claim.argumentId, nodeId: claim.nodeId });
  }

  clearSelection(): void {
    this.selected.set(null);
  }

  /**
   * Opens the evidence a chip names. A plan page opens in the Preview through the router, so the
   * reader stays in the app and in this environment; a code provision opens the code's own site in
   * a new tab, as a report's code citation does.
   */
  async openEvidence(citationIds: readonly string[]): Promise<void> {
    const citations = this.citations();
    if (!citations) return;
    const destination = chipDestination(citationIds, citations);
    const link = await this.catGraphs.link(destination);
    if (link.kind === 'none') {
      // Said aloud for a screen reader and shown for everyone else, so a click is never silent.
      this.announcement.set(link.reason);
      this.snackBar.open(link.reason, 'Dismiss', { duration: 5000 });
      return;
    }
    if (link.kind === 'external') {
      window.open(link.href, '_blank', 'noopener,noreferrer');
      this.announcement.set('The code provision opened in a new tab.');
      return;
    }
    if (destination.kind === 'page' && destination.otherPages > 0) {
      ClientLoggingService.log('INFO', '[CatGraphs] Evidence spans more pages; opening the first:', citationIds);
    }
    await this.router.navigate(link.commands, { queryParams: link.queryParams });
  }

  private syncFromUrl(): void {
    const projectId = this.projectId();
    if (!projectId) return;
    const match = this.router.url.match(GRAPH_ID_IN_URL);
    const graphId = match ? decodeURIComponent(match[1]) : '';
    this.graphId.set(graphId);
    if (projectId !== this.loadedProject) {
      this.loadedProject = projectId;
      void this.loadList(projectId);
    }
    if (graphId && `${projectId}/${graphId}` !== this.requestedGraph) {
      this.requestedGraph = `${projectId}/${graphId}`;
      void this.loadGraph(projectId, graphId);
    }
    if (!graphId) this.requestedGraph = '';
  }

  private async loadList(projectId: string): Promise<void> {
    // Another project's graphs must not show while this project's list loads.
    this.summaries.set([]);
    this.listLoading.set(true);
    this.listError.set('');
    try {
      const summaries = await this.catGraphs.list(projectId);
      if (projectId === this.projectId()) this.summaries.set(summaries);
    } catch (error) {
      ClientLoggingService.log('WARN', '[CatGraphs] Listing the graphs failed:', projectId, error);
      if (projectId === this.projectId()) {
        this.summaries.set([]);
        this.listError.set("This project's graphs could not be listed. Please try again later.");
      }
    } finally {
      this.listLoading.set(false);
    }
  }

  private async loadGraph(projectId: string, graphId: string): Promise<void> {
    const key = `${projectId}/${graphId}`;
    this.graph.set(null);
    this.picture.set(null);
    this.selected.set(null);
    this.graphError.set('');
    this.pictureError.set('');
    this.mode.set('picture');
    this.zoom.set('fit');
    this.graphLoading.set(true);
    this.pictureLoading.set(true);

    // The graph and its picture are asked for together; the list of claims needs only the graph.
    const picture = this.catGraphs.picture(projectId, graphId).then(
      svg => ({ svg }),
      (error: unknown) => ({ error })
    );
    try {
      const graph = await this.catGraphs.get(projectId, graphId);
      if (key !== this.requestedGraph) return;
      this.graph.set(graph);
    } catch (error) {
      ClientLoggingService.log('WARN', '[CatGraphs] Loading the graph failed:', key, error);
      if (key === this.requestedGraph) this.graphError.set('This graph could not be loaded. It may have been removed.');
    } finally {
      if (key === this.requestedGraph) this.graphLoading.set(false);
    }

    const result = await picture;
    if (key !== this.requestedGraph) return;
    this.pictureLoading.set(false);
    if ('error' in result) {
      ClientLoggingService.log('WARN', '[CatGraphs] The picture could not be fetched:', key, result.error);
      this.pictureError.set('The picture of this graph is not available right now, so its claims are listed instead.');
      this.mode.set('list');
      return;
    }
    const clean = cleanCatGraphPicture(result.svg);
    if (!clean) {
      this.pictureError.set('The picture of this graph could not be shown, so its claims are listed instead.');
      this.mode.set('list');
      return;
    }
    this.picture.set(clean);
  }
}
